import { useApi } from "../useApi";
import { isErr } from "../../Utils/Result";
import { CreateDueDiligenceCaseRequest, UpdateDueDiligenceCaseRequest, DocumentType, DueDiligenceTier } from "../../Types-DTOs/DueDiligence";

export const useDueDiligence = () => {
  const {
    createDueDiligenceCaseEndpoint,
    getDueDiligenceCaseEndpoint,
    listDueDiligenceCasesEndpoint,
    updateDueDiligenceCaseEndpoint,
    uploadDueDiligenceDocumentEndpoint,
    listDueDiligenceDocumentsEndpoint,
    getDocumentSignedUrlEndpoint,
    deleteDueDiligenceDocumentEndpoint,
    getDueDiligencePaymentUrlEndpoint,
    getDueDiligenceUpsellPaymentUrlEndpoint,
    getDueDiligenceUpsellQuoteEndpoint,
    verifyDueDiligencePaymentEndpoint,
    payDueDiligenceCaseWithCreditsEndpoint,
    getDueDiligenceCreditDiscountEndpoint,
    applyDueDiligenceCreditDiscountEndpoint,
    upgradeDueDiligenceCaseWithCreditsEndpoint,
    getReportManifestEndpoint,
    saveHiddenPdfSectionsEndpoint,
    getReportNumberedSectionEndpoint,
    translateReportEndpoint,
    getReportFilesEndpoint,
    exportDueDiligencePdfEndpoint,
    getDueDiligencePricingEndpoint,
    getDueDiligenceChatSuggestionsEndpoint,
  } = useApi();

  const createCase = async (data: CreateDueDiligenceCaseRequest) => {
    return await createDueDiligenceCaseEndpoint(data);
  };

  const getCase = async (caseId: string) => {
    return await getDueDiligenceCaseEndpoint(caseId);
  };

  const listCases = async (filters?: { status?: string; includeArchived?: boolean }) => {
    return await listDueDiligenceCasesEndpoint(filters);
  };

  const updateCase = async (caseId: string, data: UpdateDueDiligenceCaseRequest) => {
    return await updateDueDiligenceCaseEndpoint(caseId, data);
  };

  const uploadDocument = async (caseId: string, file: File, documentType: DocumentType) => {
    return await uploadDueDiligenceDocumentEndpoint(caseId, file, documentType);
  };

  const listDocuments = async (caseId: string) => {
    return await listDueDiligenceDocumentsEndpoint(caseId);
  };

  const getDocumentSignedUrl = async (caseId: string, documentId: string) => {
    return await getDocumentSignedUrlEndpoint(caseId, documentId);
  };

  const deleteDocument = async (caseId: string, documentId: string) => {
    return await deleteDueDiligenceDocumentEndpoint(caseId, documentId);
  };

  const getPaymentUrl = async (caseId: string, tier: DueDiligenceTier) => {
    return await getDueDiligencePaymentUrlEndpoint(caseId, tier);
  };

  const getUpsellPaymentUrl = async (caseId: string, toTier: DueDiligenceTier, promotionCode?: string) => {
    return await getDueDiligenceUpsellPaymentUrlEndpoint(caseId, toTier, promotionCode);
  };

  const getUpsellQuote = async (caseId: string, toTier: DueDiligenceTier, promotionCode?: string) =>
    getDueDiligenceUpsellQuoteEndpoint(caseId, toTier, promotionCode);

  const verifyPayment = async (caseId: string, sessionId: string) => {
    return await verifyDueDiligencePaymentEndpoint(caseId, sessionId);
  };

  const payWithCredits = async (caseId: string) => {
    return await payDueDiligenceCaseWithCreditsEndpoint(caseId);
  };

  const getCreditDiscount = async (caseId: string) => getDueDiligenceCreditDiscountEndpoint(caseId);

  const applyCreditDiscount = async (caseId: string) => applyDueDiligenceCreditDiscountEndpoint(caseId);

  const upgradeWithCredits = async (caseId: string, toTier: DueDiligenceTier) =>
    upgradeDueDiligenceCaseWithCreditsEndpoint(caseId, toTier);

  const saveHiddenPdfSections = async (caseId: string, hiddenSections: string[]) =>
    saveHiddenPdfSectionsEndpoint(caseId, hiddenSections)

  const getReportManifest = async (caseId: string, language?: string) =>
    getReportManifestEndpoint(caseId, language)

  const getReportNumberedSection = async (
    caseId: string,
    sectionNumber: string,
    language?: string,
  ) => getReportNumberedSectionEndpoint(caseId, sectionNumber, language)

  const translateReport = async (caseId: string, language: string) =>
    translateReportEndpoint(caseId, language)

  const getReportFiles = async (caseId: string) => {
    return await getReportFilesEndpoint(caseId);
  };

  const exportPdf = async (caseId: string, filename: string, language?: string, excludedSections: string[] = []) => {
    const result = await exportDueDiligencePdfEndpoint(caseId, language, excludedSections);
    if (!isErr(result)) {
      const url = URL.createObjectURL(result.value);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    }
    return result;
  };

  const getDueDiligencePricing = async () => {
    return await getDueDiligencePricingEndpoint();
  };

  const getChatSuggestions = async (caseId: string) => {
    return await getDueDiligenceChatSuggestionsEndpoint(caseId);
  };

  return {
    createCase,
    getCase,
    listCases,
    updateCase,
    uploadDocument,
    listDocuments,
    getDocumentSignedUrl,
    deleteDocument,
    getPaymentUrl,
    getUpsellPaymentUrl,
    getUpsellQuote,
    verifyPayment,
    payWithCredits,
    getCreditDiscount,
    applyCreditDiscount,
    upgradeWithCredits,
    getReportManifest,
    saveHiddenPdfSections,
    getReportNumberedSection,
    translateReport,
    getReportFiles,
    exportPdf,
    getDueDiligencePricing,
    getChatSuggestions,
  };
};